export const CHECKOUT_RETURN_PARAM = "returnTo";

/** Where checkout was opened from: Settings (`/dashboard/checkout`) or the Automations tab. */
export const AUTOMATIONS_CHECKOUT_SURFACES = [
  "settings",
  "automations_tab",
] as const;
export type AutomationsCheckoutSurface =
  (typeof AUTOMATIONS_CHECKOUT_SURFACES)[number];

const dashboardPrefix = (slug: string) => `/${slug}/dashboard/`;
const settingsCheckoutPath = (slug: string) =>
  `${dashboardPrefix(slug)}checkout`;
const tabCheckoutPath = (slug: string) =>
  `${automationsTabHref(slug)}/checkout`;

const isUnder = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

export function automationsTabHref(comedianSlug: string): string {
  return `${dashboardPrefix(comedianSlug)}messages/automations`;
}

/** A `returnTo` inside the Automations tab opens the in-tab checkout; anything else opens Settings' checkout. */
export function automationsCheckoutHref(
  comedianSlug: string,
  returnTo?: string
): string {
  const returnPath = returnTo?.split("?")[0];
  const base =
    returnPath && isUnder(returnPath, automationsTabHref(comedianSlug))
      ? tabCheckoutPath(comedianSlug)
      : settingsCheckoutPath(comedianSlug);
  return returnTo
    ? `${base}?${CHECKOUT_RETURN_PARAM}=${encodeURIComponent(returnTo)}`
    : base;
}

/** Same-site dashboard path to return to after checkout; anything else (open
 *  redirects, other comedians, either checkout page itself) falls back to Plan &
 *  usage from Settings, or to the tab from the tab. */
export function resolveCheckoutReturnTo(
  raw: string | null | undefined,
  comedianSlug: string,
  surface: AutomationsCheckoutSurface = "settings"
): string {
  const fallback =
    surface === "automations_tab"
      ? automationsTabHref(comedianSlug)
      : `${dashboardPrefix(comedianSlug)}plan-usage`;
  if (
    !raw ||
    !raw.startsWith("/") ||
    raw.startsWith("//") ||
    // eslint-disable-next-line no-control-regex
    /[\\\u0000-\u001f\u007f]/.test(raw)
  ) {
    return fallback;
  }
  const base = "https://x.invalid";
  let url: URL;
  try {
    url = new URL(raw, base);
  } catch {
    return fallback;
  }
  if (
    url.origin !== base ||
    !url.pathname.startsWith(dashboardPrefix(comedianSlug)) ||
    url.pathname.startsWith(settingsCheckoutPath(comedianSlug)) ||
    isUnder(url.pathname, tabCheckoutPath(comedianSlug))
  ) {
    return fallback;
  }
  return url.pathname + url.search;
}
